import { ReactNode, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  AlertProps,
} from "@chakra-ui/react";
import { serverGql } from "__generated__/serverGql";
import { StripePaymentStatus } from "__generated__/serverGql/graphql";
import ContactEmailLink from "components/Shared/ContactEmailLink";
import serverClient from "utils/apollo/serverClient";
import { AssertionError, assertPresence } from "utils/assertions";
import { asyncVoid } from "utils/asyncVoidHandler";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { useUpdateBrandSubscriptionCache } from "./useRefreshSubscriptionStatus";

const processCheckoutMutation = serverGql(/* ServerGraphQL */ `
  mutation ProcessStripeCheckoutSuccess($brandId: UUID!, $sessionId: String!) {
    processStripeCheckoutSuccess(brandId: $brandId, sessionId: $sessionId) {
      paymentStatus
      brand {
        id
        isSubscriptionValid
        stripeSubscriptionId
        ...RefreshBrandSubscriptionFields
      }
    }
  }
`);

type Props = {
  sessionId: string;
  onProcessed: () => void;
};

export default function CheckoutSessionCompletionAlert({
  sessionId,
  onProcessed,
}: Props) {
  const brandId = useCurrentBrandId();
  const hasProcessedRef = useRef(false);
  const updateBrandSubscriptionCache = useUpdateBrandSubscriptionCache();
  const [processSession, { data, loading, error }] = useMutation(
    processCheckoutMutation,
    { client: serverClient },
  );

  useEffect(() => {
    if (!brandId || !sessionId || hasProcessedRef.current) {
      return;
    }

    hasProcessedRef.current = true;

    asyncVoid(
      processSession({ variables: { brandId, sessionId } }).then((result) => {
        // Update the PostGraphile Apollo cache with the brand data from server mutation
        const brand = result.data?.processStripeCheckoutSuccess?.brand;
        assertPresence(brand, "No brand returned from mutation");
        updateBrandSubscriptionCache(brand);

        onProcessed();
      }),
    );
  }, [
    sessionId,
    brandId,
    processSession,
    onProcessed,
    updateBrandSubscriptionCache,
  ]);

  const alertConfig = ((): Pick<AlertProps, "status"> & {
    title: ReactNode;
    description: ReactNode;
  } => {
    if (loading) {
      return {
        status: "loading",
        title: "Processing your subscription…",
        description: "Please wait while we set up your billing.",
      };
    }

    const processResult = data?.processStripeCheckoutSuccess;

    if (error || !processResult) {
      return {
        status: "error",
        title: "Unable to process subscription",
        description: error?.message || "Failed to process checkout session",
      };
    }

    if (
      processResult.paymentStatus === StripePaymentStatus.PAID ||
      processResult.paymentStatus === StripePaymentStatus.NO_PAYMENT_REQUIRED
    ) {
      return {
        status: "success",
        title: "Subscription activated!",
        description:
          "Your subscription has been successfully activated. Thank you!",
      };
    }

    if (processResult.paymentStatus === StripePaymentStatus.UNPAID) {
      return {
        status: "warning",
        title: "Payment incomplete",
        description: (
          <>
            Your payment is still being processed. Some payment methods, like
            ACH, can take time to process. If your payment method requires
            additional steps, please check your email for instructions from
            Stripe. If you continue to have issues, please contact{" "}
            <ContactEmailLink fontWeight="semibold" /> for assistance.
          </>
        ),
      };
    }

    // This should never happen - we handle all three enum values above
    throw new AssertionError(
      `Unexpected payment status: ${processResult.paymentStatus as string}`,
    );
  })();

  return (
    <Alert status={alertConfig.status} my={8}>
      <AlertIcon />
      <Box>
        <AlertTitle>{alertConfig.title}</AlertTitle>
        <AlertDescription>{alertConfig.description}</AlertDescription>
      </Box>
    </Alert>
  );
}
